/**
 * Quando o convite de avaliação pode aparecer.
 *
 * Cópia deliberada da forma do `usePushPromptGate` — as condições de "agora é
 * uma hora boa para um overlay" já foram decididas uma vez nesta casa
 * (rota segura + celebrationGate + assentamento), e duplicá-las com outro
 * critério é como as duas telas de busca divergiram (BUG-81).
 *
 * A diferença é o que NÃO tem: nenhum recheck assíncrono (não há plugin nem
 * permissão a consultar — abrir a loja é um link), então o veredito do timer é
 * um recheck síncrono de `canOfferReviewPrompt`, que cobre quem dispensou por
 * outra aba/rota no meio do assentamento.
 *
 * A PRIORIDADE atrás do convite de push não mora aqui — mora no watcher, que é
 * quem tem o perfil em mãos para responder `canOfferPushPrompt`.
 */

import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  isCelebrationBlocked,
  subscribeCelebrationGate,
} from "../../gamification/utils/celebrationGate";
import { isSafeCelebrationRoute } from "../../gamification/hooks/useBadgeUnlockQueue";
import { canOfferReviewPrompt } from "../utils/reviewPrompt";

/** O mesmo assentamento do convite de push: o app está PEDINDO algo. */
const SETTLE_MS = 1500;

export function useReviewPromptGate(eligible: boolean): {
  open: boolean;
  close: () => void;
} {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [blocked, setBlocked] = useState<boolean>(() => isCelebrationBlocked());
  // Respondido nesta sessão não reabre nem que a rota mude; a persistência
  // (snooze/done) cobre as próximas. ESTADO, não ref: `canOpen` é derivado no
  // render, e ler ref no render é o que `react-hooks/refs` (com razão)
  // reprova — o gate do push só escapa da mesma regra por bailout do
  // analisador no `import()` dinâmico dele.
  const [answered, setAnswered] = useState(false);

  useEffect(() => subscribeCelebrationGate(setBlocked), []);

  const canOpen =
    eligible &&
    !open &&
    !answered &&
    !blocked &&
    isSafeCelebrationRoute(pathname) &&
    canOfferReviewPrompt();

  useEffect(() => {
    if (!canOpen) return;
    const timer = setTimeout(() => {
      if (canOfferReviewPrompt()) setOpen(true);
    }, SETTLE_MS);
    return () => clearTimeout(timer);
  }, [canOpen, pathname]);

  const close = useCallback(() => {
    setAnswered(true);
    setOpen(false);
  }, []);

  return { open, close };
}
